//imports
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { disableIdleLockSwitch, enableIdleLockSwitch, displayNotice, RootState, AppDispatch } from "../../../../globals";
import { StudentProgressRowDTO, StudentProgressSummaryDTO } from "../../../../models/StudentProgressDTO";
import { AttendanceModalState, ProgressChip, ProgressTab, ProgressFilter, ScheduleOption, SIN_PROFESOR, StructuredFilterOptions, StructuredFilters, WhatsappModalState } from "./interfaces";
import { horarioBonito } from "../../../../utils/horarioBonito";
import S from "./strings";

//services
import HttpManager from "../../../../services/HttpManager";

//view
import StudentProgressView from "./studentProgressView";

//messages
const noCampuses = "No hay planteles registrados o asignados al rol en uso por el usuario, por lo que el acceso a este módulo permanecerá deshabilitado.";
const initError = "La búsqueda inicial de datos no pudo ser completada.";
const attendancesError = "No se pudieron cargar las asistencias.";

//constants
const PAGE_SIZE = 50;

const resumenVacio: StudentProgressSummaryDTO = {
	total: 0, rotating: 0, composite: 0, withAlert: 0, absent: 0, absentWeek: 0, overHours: 0, completed: 0, graduating: 0
};

const filtrosVacios: StructuredFilters = { mentor: "", course: "", period: "", scheduleIds: [] };

const whatsappCerrado: WhatsappModalState = { row: null, template: "" };

const asistenciasCerrado: AttendanceModalState = { row: null, loading: false, detail: null, error: null };

const profesorDe = (row: StudentProgressRowDTO): string => row.mentorName || SIN_PROFESOR;

const ESTADO_HORARIO: { [id: number]: string } = { 0: "Sin iniciar", 1: "Activo", 2: "Fusionado", 3: "Terminado", 4: "Cerrando" };

const tieneAlerta = (row: StudentProgressRowDTO, code: string): boolean =>
	Array.isArray(row.alerts) && row.alerts.some(a => a.code === code);

const pasaFiltro = (row: StudentProgressRowDTO, filter: ProgressFilter): boolean => {
	switch (filter) {
		case "rotativos": return row.isRotating === true;
		case "compuestos": return row.isComposite === true;
		case "conAlerta": return Array.isArray(row.alerts) && row.alerts.length > 0;
		case "sinAsistir": return tieneAlerta(row, "absent");
		case "faltoSemana": return tieneAlerta(row, "absent_week");
		case "horasDeMas": return tieneAlerta(row, "over_hours");
		case "cubierto": return tieneAlerta(row, "completed");
		case "porGraduarse": return row.graduatingSoon === true || tieneAlerta(row, "graduating");
		case "bajoMinimo": return tieneAlerta(row, "below_minimum");
		default: return true;
	}
};

const pasaEstructurados = (row: StudentProgressRowDTO, f: StructuredFilters): boolean =>
	(!f.mentor || profesorDe(row) === f.mentor)
	&& (!f.course || row.courseName === f.course)
	&& (!f.period || row.periodName === f.period)
	&& (f.scheduleIds.length === 0 || f.scheduleIds.includes(row.scheduleId));

/* Sin acentos ni mayusculas, para que "Jose" encuentre a "José". */
const normalizar = (texto: any): string =>
	String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const distintos = (valores: (string | null | undefined)[]): string[] =>
	Array.from(new Set(valores.filter((v): v is string => Boolean(v)))).sort((a, b) => a.localeCompare(b, "es"));

const csvCelda = (valor: any): string => {
	const texto = valor == null ? "" : String(valor);
	return /[",;\n\r]/.test(texto) ? `"${texto.replace(/"/g, "\"\"")}"` : texto;
};

const StudentProgressController = () => {

	//init dispatch
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	//init http
	const http = HttpManager.getInstance();

	//useSelector
	const sessionToken: string | null = useSelector((state: RootState) => state.sessionToken.value);
	const idleLocked: boolean = useSelector((state: RootState) => state.idleLockSwitch.value);
	//El plantel es el que muestra el encabezado de WAPP (campusRef[currentUserCampus]);
	//la sesion v2 (sessionState) solo entra si ese aun no esta.
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const currentCampusId: number = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId);
	const currentCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);

	const campusHeader: any = campusRef && currentUserCampus != null ? campusRef[currentUserCampus] : null;
	const campusId: number = (campusHeader && campusHeader.id) || currentCampusId || 0;
	const campusName: string = (campusHeader && campusHeader.name) || (currentCampus && currentCampus.name) || "";

	//useState
	const [sectionLoaded, setSectionLoaded] = useState<boolean>(false);
	const [rows, setRows] = useState<StudentProgressRowDTO[]>([]);
	const [summary, setSummary] = useState<StudentProgressSummaryDTO>(resumenVacio);
	const [filter, setFilter] = useState<ProgressFilter>("todos");
	const [includeInactive, setIncludeInactive] = useState<boolean>(false);
	const [includeInactiveStudents, setIncludeInactiveStudents] = useState<boolean>(false);
	const [attendance, setAttendance] = useState<AttendanceModalState>(asistenciasCerrado);
	const [structured, setStructured] = useState<StructuredFilters>(filtrosVacios);
	const [search, setSearch] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [gradesRow, setGradesRow] = useState<StudentProgressRowDTO | null>(null);
	const [whatsapp, setWhatsapp] = useState<WhatsappModalState>(whatsappCerrado);
	const [tab, setTab] = useState<ProgressTab>(() => new URLSearchParams(window.location.search).get("pestana") === "reponer" ? "reponer" : "progreso");
	const currencyCode: string = (campusHeader && campusHeader.company && campusHeader.company.currency && campusHeader.company.currency.code) || "";
	const recoveryOpen: number = useMemo(() => rows.reduce((acc, r) => acc + (Number(r.recoveryOpen) || 0), 0), [rows]);

	//functions
	const displayError = (message: string) => {
		dispatch(
			displayNotice({
				cornerClose: false,
				message: message,
				heading:
					<h3 style={{ color: "#A8730A", display: "inline-block" }}>
						Error
					</h3>
			})
		);
	};

	/* Se carga solo con los dos interruptores (horarios / alumnos no activos); chips, selects y buscador filtran en el navegador. */
	const getProgress = async () => {
		if (!campusId) return;

		try {
			dispatch(enableIdleLockSwitch());
			setRows([]);
			const result = await http.studentProgressService.getStudentProgress(campusId, { includeInactive, includeInactiveStudents });

			if (result.status == 200 || result.status == 204) {
				const data: any = result.data || {};
				const filas: StudentProgressRowDTO[] = Array.isArray(data.rows) ? data.rows : [];
				setRows(filas);
				setSummary({ ...resumenVacio, ...(data.summary || {}) });
				setPage(1);
				setSectionLoaded(true);
			} else {
				displayError(initError);
			}
		} catch (error) {
			console.log(error);
			displayError(initError);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	/*
		Las cuentas de los chips salen de las filas cargadas y no del resumen del
		backend, para que coincidan siempre con lo que se ve al pulsar cada uno.
	*/
	const chips: ProgressChip[] = useMemo(() => {
		const cuenta = (llave: ProgressFilter) => rows.filter(r => pasaFiltro(r, llave)).length;
		return [
			{ llave: "todos", texto: "Todos", cuenta: rows.length },
			{ llave: "rotativos", texto: "Rotativos", cuenta: cuenta("rotativos") },
			{ llave: "compuestos", texto: "Compuestos", cuenta: cuenta("compuestos") },
			{ llave: "conAlerta", texto: "Con alerta", cuenta: cuenta("conAlerta") },
			{ llave: "sinAsistir", texto: "Sin asistir 2+ semanas", cuenta: cuenta("sinAsistir") },
			{ llave: "faltoSemana", texto: "Faltó últimos 7 días", cuenta: cuenta("faltoSemana") },
			{ llave: "horasDeMas", texto: "Tomó horas de más", cuenta: cuenta("horasDeMas") },
			{ llave: "cubierto", texto: "Ya cubrió sus horas", cuenta: cuenta("cubierto") },
			{ llave: "porGraduarse", texto: "Por graduarse", cuenta: cuenta("porGraduarse") },
			{ llave: "bajoMinimo", texto: S.belowMinimumChip, cuenta: cuenta("bajoMinimo") }
		];
	}, [rows]);

	/* Opciones de profesor, curso, periodo y horario: cada una se acota a lo elegido antes. */
	const structuredOptions: StructuredFilterOptions = useMemo(() => {
		const porProfesor = structured.mentor ? rows.filter(r => profesorDe(r) === structured.mentor) : rows;
		const porCurso = structured.course ? porProfesor.filter(r => r.courseName === structured.course) : porProfesor;
		const porPeriodo = structured.period ? porCurso.filter(r => r.periodName === structured.period) : porCurso;

		//Los horarios solo se listan con curso o profesor elegido: sin acotar serían cientos de chips iguales.
		const horarios = new Map<number, ScheduleOption>();
		if (structured.course || structured.mentor) {
			porPeriodo.forEach(r => {
				if (r.scheduleId != null && !horarios.has(r.scheduleId)) {
					horarios.set(r.scheduleId, { id: r.scheduleId, name: horarioBonito(r.scheduleName) || `Horario ${r.scheduleId}`, level: r.levelName || "", period: r.periodName || "" });
				}
			});
		}
		//Si dos horarios del mismo curso se llaman igual, se distingue por módulo
		const repetidos = new Set<string>();
		const vistos = new Set<string>();
		horarios.forEach(h => { if (vistos.has(h.name)) repetidos.add(h.name); vistos.add(h.name); });
		//Repetidos: primero se agrega el módulo; si siguen iguales, también el periodo
		const conModulo = Array.from(horarios.values()).map(h => repetidos.has(h.name) && h.level ? { ...h, name: `${h.name} · ${h.level}` } : h);
		const aunRepetidos = new Set<string>();
		const vistos2 = new Set<string>();
		conModulo.forEach(h => { if (vistos2.has(h.name)) aunRepetidos.add(h.name); vistos2.add(h.name); });
		const schedules = conModulo
			.map(h => aunRepetidos.has(h.name) && h.period ? { ...h, name: `${h.name} · ${h.period}` } : h)
			.sort((a, b) => a.name.localeCompare(b.name, "es"));

		//"Sin profesor" va al final, no entre la S de los apellidos.
		const profesores = distintos(rows.map(r => r.mentorName));
		if (rows.some(r => !r.mentorName)) profesores.push(SIN_PROFESOR);

		return {
			mentors: profesores,
			courses: distintos(porProfesor.map(r => r.courseName)),
			periods: distintos(porCurso.map(r => r.periodName)),
			schedules
		};
	}, [rows, structured.mentor, structured.course, structured.period]);

	const filasFiltradas: StudentProgressRowDTO[] = useMemo(() => {
		const q = normalizar(search.trim());

		return rows
			.filter(r => pasaEstructurados(r, structured))
			.filter(r => pasaFiltro(r, filter))
			.filter(r => {
				if (!q) return true;
				const texto = normalizar(
					[r.studentName, r.identityCode, r.phone, r.courseName, r.courseCode, r.levelName, r.periodName, r.scheduleName, horarioBonito(r.scheduleName), r.mentorName]
						.filter(Boolean)
						.join(" ")
				);
				return texto.includes(q);
			})
			.sort((a, b) => normalizar(a.studentName).localeCompare(normalizar(b.studentName), "es"));
	}, [rows, structured, filter, search]);

	const pageCount: number = Math.max(1, Math.ceil(filasFiltradas.length / PAGE_SIZE));
	const paginaActual: number = Math.min(page, pageCount);
	const filasPagina: StudentProgressRowDTO[] = filasFiltradas.slice((paginaActual - 1) * PAGE_SIZE, paginaActual * PAGE_SIZE);

	const onFilter = (nuevo: ProgressFilter) => {
		setFilter(nuevo);
		setPage(1);
	};

	const onToggleInactive = () => {
		setIncludeInactive(prev => !prev);
		setStructured(filtrosVacios);
		setPage(1);
	};

	const onToggleInactiveStudents = () => {
		setIncludeInactiveStudents(prev => !prev);
		setStructured(filtrosVacios);
		setPage(1);
	};

	const onMentor = (mentor: string) => {
		setStructured({ mentor, course: "", period: "", scheduleIds: [] });
		setPage(1);
	};

	const onCourse = (course: string) => {
		setStructured(prev => ({ mentor: prev.mentor, course, period: "", scheduleIds: [] }));
		setPage(1);
	};

	const onPeriod = (period: string) => {
		setStructured(prev => ({ ...prev, period, scheduleIds: [] }));
		setPage(1);
	};

	const onToggleSchedule = (scheduleId: number) => {
		setStructured(prev => ({
			...prev,
			scheduleIds: prev.scheduleIds.includes(scheduleId)
				? prev.scheduleIds.filter(id => id !== scheduleId)
				: [...prev.scheduleIds, scheduleId]
		}));
		setPage(1);
	};

	const onClearStructured = () => {
		setStructured(filtrosVacios);
		setPage(1);
	};

	const onSearch = (valor: string) => {
		setSearch(valor);
		setPage(1);
	};

	/* CSV de lo que se ve (con filtros y busqueda aplicados), separado por coma, UTF-8 con BOM para Excel. */
	const onExport = () => {
		const encabezados = ["Alumno", "Correlativo", "Teléfono", "Estatus matrícula", "Curso", "Módulo", "Periodo", "Horario", "Profesor", "Estado del horario", "Fin del horario",
			"Tipo", "Compuesto", "Horarios autorizados", "Horas tomadas", "Horas contratadas", "Sesiones tomadas", "Sesiones totales",
			"Última asistencia", "Semanas sin ir", "Alerta", "Calificación"];

		const lineas = filasFiltradas.map(r => [
			r.studentName,
			r.identityCode,
			r.phone || "",
			r.enrollmentStatus || "",
			r.courseName,
			r.levelName,
			r.periodName,
			horarioBonito(r.scheduleName),
			profesorDe(r),
			ESTADO_HORARIO[r.scheduleStatusId] || "",
			r.scheduleEndDate || "",
			r.isRotating ? "Rotativo" : "Fijo",
			r.isComposite ? "Sí" : "No",
			(r.authorizedSchedules || []).map(s => horarioBonito(s.name)).join(" / "),
			r.takenHours,
			r.entitledHours,
			r.sessionsTaken,
			r.sessionsTotal,
			r.lastPresent || "Nunca",
			r.weeksAbsent,
			(r.alerts || []).length === 0 ? "Al día" : r.alerts.map(a => a.label).join(" / "),
			"N/D"
		].map(csvCelda).join(","));

		const contenido = "﻿" + [encabezados.map(csvCelda).join(","), ...lineas].join("\r\n");
		const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const enlace = document.createElement("a");
		enlace.href = url;
		enlace.download = `Progreso Académico${campusName ? " - " + campusName : ""}.csv`;
		document.body.appendChild(enlace);
		enlace.click();
		document.body.removeChild(enlace);
		URL.revokeObjectURL(url);
	};

	//Calificaciones: por ahora solo el marcador del modulo que viene.
	const onOpenGrades = (row: StudentProgressRowDTO) => setGradesRow(row);
	const onCloseGrades = () => setGradesRow(null);

	//Ficha del alumno: la pantalla de alumnos entiende este enlace y abre el perfil.
	const onOpenProfile = (row: StudentProgressRowDTO) => {
		navigate("/students?alumno=" + row.studentId + "&plantel=" + campusId);
	};

	/* Asistencias del alumno en esa matricula: se piden al abrir el modal, no se guardan entre filas. */
	const onOpenAttendances = async (row: StudentProgressRowDTO) => {
		if (!campusId) return;
		setAttendance({ row, loading: true, detail: null, error: null });

		try {
			const result = await http.studentProgressService.getAttendances(campusId, row.enrollmentId);
			const detail: any = result.data || null;
			setAttendance(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, detail: detail && Array.isArray(detail.classes) ? detail : null, error: detail ? null : attendancesError }
				: prev);
		} catch (error) {
			console.log(error);
			setAttendance(prev => prev.row && prev.row.enrollmentId === row.enrollmentId
				? { ...prev, loading: false, error: attendancesError }
				: prev);
		}
	};

	const onCloseAttendances = () => setAttendance(asistenciasCerrado);

	const onOpenWhatsapp = (row: StudentProgressRowDTO) => setWhatsapp({ ...whatsappCerrado, row });

	const onCloseWhatsapp = () => setWhatsapp(whatsappCerrado);

	const onPickTemplate = (key: string) => setWhatsapp(prev => ({ ...prev, template: prev.template === key ? "" : key }));

	//useEffect
	useEffect(() => {
		if (sessionToken) {
			campusId ? getProgress() : displayError(noCampuses);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, includeInactive, includeInactiveStudents]);

	//Al cambiar de plantel se cierran los modales.
	useEffect(() => {
		setWhatsapp(whatsappCerrado);
		setGradesRow(null);
		setAttendance(asistenciasCerrado);
	}, [campusId]);

	return (
		<StudentProgressView
			sectionLoaded={sectionLoaded}
			campusName={campusName}
			idleLocked={idleLocked}
			summary={summary}
			chips={chips}
			filter={filter}
			onFilter={onFilter}
			includeInactive={includeInactive}
			onToggleInactive={onToggleInactive}
			includeInactiveStudents={includeInactiveStudents}
			onToggleInactiveStudents={onToggleInactiveStudents}
			structured={structured}
			structuredOptions={structuredOptions}
			onMentor={onMentor}
			onCourse={onCourse}
			onPeriod={onPeriod}
			onToggleSchedule={onToggleSchedule}
			onClearStructured={onClearStructured}
			search={search}
			onSearch={onSearch}
			rows={filasPagina}
			totalFiltered={filasFiltradas.length}
			page={paginaActual}
			pageCount={pageCount}
			pageSize={PAGE_SIZE}
			onPage={setPage}
			onExport={onExport}
			gradesRow={gradesRow}
			onOpenGrades={onOpenGrades}
			onCloseGrades={onCloseGrades}
			onOpenProfile={onOpenProfile}
			attendance={attendance}
			onOpenAttendances={onOpenAttendances}
			onCloseAttendances={onCloseAttendances}
			whatsapp={whatsapp}
			onOpenWhatsapp={onOpenWhatsapp}
			onCloseWhatsapp={onCloseWhatsapp}
			onPickTemplate={onPickTemplate}
			campusId={campusId}
			currencyCode={currencyCode}
			tab={tab}
			onTab={setTab}
			recoveryOpen={recoveryOpen}
			onRecoveriesChanged={getProgress}
		/>
	);
}

export default StudentProgressController
